<!-- 
WHAT: A component to simulate BtDashboardSettings for csDashboard.
WHO: Behzad Talebpour (btalebpour@mindfireinc.com)
HISTORY:
- V260211.1: Changed the entire logic based on the new business logic.
- V260105.1: [Didn't send to Aref] Used optional chaining (?.) for sharedWith to eliminate console errors.
- V251027.1: Added confirmMessage prop to be shown when the btn is clicked.
- V230119.1: Applied/Modified styles to display contents properly after Aref's upgrade.
- 03/23/22(B0.2): Placed sharedWith section in <v-expansion-panels>.
- 03/22/22(B0.1): 1st release (Copied from BtDashboardSharing in the helpers project, then was modified according to the business logic).
BUSINESS LOGIC - new:
   1) If your account or parent account are in aid[] or paid[] is matched and the email[] is empty or contains the login email you have access to the Dashboard
   2) No more empty aid[] or paid[] array means all
   3) If the author of the dashboard is regular user, they can just share within current or parent account means aid['currentAccountId'] and paid['parentAccountID']
   4) If the author of the dashboard is developer user (kushal, manasa, ...), they can set aid[] and paid[] with any accounts or parents
 
    --------example 1------------
    aid[1], paid['11'], email[]
    This means in order to see the dashboard either your account id should be '1' or your parent account ID is '11'
 
    --------example 2------------
    aid['1'], paid['11'], email['rmemarian@mindfireinc.com']
    This means in order to see the dashboard either your account id should be '1' or your parent account ID is '11' and the logged in email should be 'rmemarian@mindfireinc.com'
 
    --------example 3------------
    aid['11'], paid['11'], email[]
    This means all the sub-accounts of parent 11 and parent itself can use the dashboard
BUSINESS LOGIC:
- Display a btn. Click will open a dialog with designated fields.
- Actions: Save/Update, Saveas, Delete
- Initial fields from the provided dashboard object.
- @submit, return the updated object.
- Need 1 tag
- If pa==true, can be shared with everyone (empty array). Or else, 1 paid, any aid and/or email
- aids can be empty
- paids required and can be one only (for non-admin)
-->
<template>
<v-container fluid class="py-0 px-0">
   <v-btn dark small
      :color="btnColor"
      :block="btnBlock"
      :disabled="btnDisabled || (btnRole === 'delete' && !value.name)"
      @click="btnClicked"
   >
      <v-icon v-if="btnIconLeft"
         dark small left
      >{{btnIconLeft}}</v-icon>
      {{btnTitle}}
      <v-icon v-if="btnIconRight"
         dark small right
      >{{btnIconRight}}</v-icon>
   </v-btn>

   <v-dialog no-click-animation persistent scrollable
      max-width="480px"
      v-model="dialogSave"
   >
      <v-card>
         <v-card-title class="title grey--text darken-4 font-weight-bold pt-3 pb-0">{{myDialogTitle}}</v-card-title>
         <v-card-text class="pb-0">
            <div v-if="errMsg" class="red--text"><pre>{{errMsg}}</pre></div>
            <v-form lazy-validation
               ref="formSave"
               v-model="isFormSaveValid"
            >
               <v-row class="pt-3 pb-2">
                  <v-col cols="12" class="py-0">
                     <v-text-field dense persistent-hint required
                        ref="newDashName"
                        :hint="newDashName ? 'Dashboard Name' :''"
                        placeholder="enter a new name for this dashboard"
                        :disabled="isNewDashNameDisabled"
                        :rules="newDashNameRules"
                        v-model="newDashName"
                     ></v-text-field>
                  </v-col>
               </v-row>

               <v-row class="pt-2 pb-0">
                  <v-col cols="12" class="pt-0">
                     <v-autocomplete dense hide-selected multiple persistent-hint small-chips deletable-chips solo
                        class="px-0"
                        :hint="newTags.length ? 'Tags' :''"
                        label="select one or more tags..."
                        :counter="tagItems.length"
                        :items="tagItems"
                        :rules="[rules.arrayRequired]"
                        v-model="newTags"
                        @change="tagsChanged"
                     ></v-autocomplete>
                  </v-col>
               </v-row>

               <v-expansion-panels multiple focusable
                  class="pt-3 pb-2"
                  v-model="panels"
               >
                  <v-expansion-panel readonly>
                     <v-expansion-panel-header hide-actions>
                        <strong>Share Dashboard With:</strong>
                     </v-expansion-panel-header>
                     <v-expansion-panel-content class="py-5">
                        <v-row class="py-0 px-0">
                           <v-col cols="12" class="py-0">
                              <v-select dense persistent-hint hide-selected
                                 ref="paid"
                                 class="pt-0"
                                 placeholder="select parent accounts..."
                                 :hint="formValues.paid ? 'Parent Accounts' : ''"
                                 :items="paidItems"
                                 :rules="[rules.required]"
                                 v-model="formValues.paid"
                                 @change="paidChanged"
                              ></v-select>
                           </v-col>
                           <v-col v-if="formValues.paid === 'current' || formValues.paid === 'custom'"
                              cols="12" class="py-0"
                           >
                              <v-textarea outlined persistent-hint
                                 ref="paidTextarea"
                                 class="pt-3 pb-0 body-2"
                                 label="Specified Parent Accounts"
                                 :hide-details="false"
                                 :rows="formValues.paid?.startsWith('current') ? 1 : 2"
                                 :hint="getHint(mySharedWith.paids, 'account ID')"
                                 :disabled="formValues.paid === 'current'"
                                 :rules="[rules.required, rules.numbers, rules.duplicate]"
                                 v-model="mySharedWith.paids"
                              ></v-textarea>
                           </v-col>
                        </v-row>

                        <v-row class="py-0">
                           <v-col cols="12" class="py-0">
                              <v-select dense persistent-hint hide-selected
                                 ref="said"
                                 class="pt-0"
                                 placeholder="select accounts..."
                                 :hint="formValues.said ? 'Accounts' : ''"
                                 :items="saidItems"
                                 :rules="[rules.required]"
                                 v-model="formValues.said"
                                 @change="saidChanged"
                              ></v-select>
                           </v-col>
                           <v-col v-if="formValues.said?.startsWith('current') || formValues.said === 'custom'"
                              cols="12" class="py-0"
                           >
                              <v-textarea outlined persistent-hint
                                 ref="saidTextarea"
                                 class="pt-3 pb-0 body-2"
                                 label="Specified Accounts"
                                 :disabled="formValues.said?.startsWith('current')"
                                 :hide-details="false"
                                 :hint="getHint(mySharedWith.saids, 'account ID')"
                                 :rows="formValues.said?.startsWith('current') ? 1 : 2"
                                 :rules="[rules.required, rules.numbers, rules.duplicate]"
                                 v-model="mySharedWith.saids"
                              ></v-textarea>
                           </v-col>
                        </v-row>

                        <v-row class="pt-1 pb-0">
                           <v-col cols="12" class="py-0">
                              <v-select dense persistent-hint hide-selected
                                 ref="user"
                                 class="pt-0"
                                 placeholder="select users..."
                                 :hint="formValues.user ? 'Users' : ''"
                                 :items="userItems"
                                 :rules="[rules.required]"
                                 v-model="formValues.user"
                                 @change="userChanged"
                              ></v-select>
                           </v-col>
                           <v-col v-if="formValues.user === 'current' || formValues.user==='custom'"
                              cols="12" class="py-0"
                           >
                              <v-textarea outlined persistent-hint
                                 ref="userTextarea"
                                 class="pt-3 pb-0 body-2"
                                 label="Specified Users"
                                 :disabled="formValues.user === 'current'"
                                 :hide-details="false"
                                 :hint="getHint(mySharedWith.users, 'email')"
                                 :rows="formValues.user === 'current' ? 1 : 2"
                                 :rules="[rules.required, rules.emails, rules.duplicate]"
                                 v-model="mySharedWith.users"
                              ></v-textarea>
                           </v-col>
                        </v-row>
                     </v-expansion-panel-content>
                  </v-expansion-panel>
               </v-expansion-panels>
            </v-form>
         </v-card-text>

         <v-card-actions class="pt-0 pb-0 pr-3">
            <div class="flex-grow-1"></div>
            <v-btn text
               color="blue darken-1"
               class="pr-0"
               @click="closeDialogSave"
            >Cancel</v-btn>
            <!-- :disabled="!newDashName || newDashName.toLowerCase() === value.name.toLowerCase()" -->
            <v-btn text
               color="blue darken-1"
               class="ml-0 pl-2"
               :disabled="!isFormSaveValid"
               @click="saveClicked"
            >Save</v-btn>
         </v-card-actions>
      </v-card>
   </v-dialog>

   <v-dialog no-click-animation persistent scrollable
      max-width="480px"
      v-model="dialogDelete"
   >
      <v-card>
         <v-card-title class="title grey--text darken-4 font-weight-bold pt-3 pb-0">Deleting '{{value.name}}':
         </v-card-title>
         <v-card-text class="pb-0">
            <div v-if="errMsg" class="red--text">{{errMsg}}</div>
            <v-form ref="formDelete" lazy-validation>
               <v-row class="pt-5 pb-0">
                  <v-col cols="12" class="pb-0">
                     <v-text-field outlined required
                        class="pb-0"
                        ref="currDashName"
                        label="* Dashboard Name"
                        placeholder="enter the name of dashboard that you want to delete"
                        v-model="currDashName"
                     ></v-text-field>
                  </v-col>
               </v-row>
            </v-form>
         </v-card-text>

         <v-card-actions class="pt-0 pr-4">
            <div class="flex-grow-1"></div>
            <v-btn text
               color="blue darken-1"
               @click="closeDialogDelete"
            >Cancel</v-btn>
            <v-btn text
               color="blue darken-1"
               :disabled="!currDashName || currDashName.toLowerCase() != value.name.toLowerCase()"
               @click="deleteClicked"
            >Delete</v-btn>
         </v-card-actions>
      </v-card>
   </v-dialog>

   <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
   </v-overlay>

</v-container>
</template>

<script>
import { developersAccess } from '../mixins/cs-apps.js';

const NAME = 'BtDashboardSave';
const EMAIL_PATTERN = /^(([^<>()\[\]\\.,;:\s@']+(\.[^<>()\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const NUMBER_PATTERN = /^[1-9]{1}[0-9]{0,}$/;
const FORM_VALUES = {
   paid: '',
   said: '',
   user: ''
};
let DEBUG;

class SharedWith {
   constructor(initVal) {
      if (!initVal)
         initVal = {};

      this.paids = _setValue(initVal.paids);
      this.saids = _setValue(initVal.aids);
      this.users = _setValue(initVal.users);
   }
}

function _setValue(val) {
   if (Array.isArray(val) && val.length > 0)
      return val.join(', ');
   else return '';
}

function _log(msg, log) {
   if (DEBUG || log) console.log(`-----${NAME} V260211.1 says => ${msg}`);
}

function _stringToArray(strVal, type) {
   const result = [];
   if (strVal) {
      strVal.split(',').forEach(sv => {
         const svTrimmed = sv.replace(/\s/g,'');
         if (type==='int')
            result.push(Number(svTrimmed));
         else
            result.push(svTrimmed);
      });
   }
   return result;
}

export default {
   name: NAME,

   props: {
      // { name: '', author: '', tags: [], sharedWith: {aids: [String], paids: [String], users: []} }
      value: {
         type: Object,
         required: true
      },
      btnBlock: {
         type: Boolean,
         default: false
      },
      btnColor: {
         type: String,
         default: "primary"
      },
      btnDisabled: {
         type: Boolean,
         default: false
      },
      btnIconLeft: {
         type: String,
         default: "save"
      },
      btnIconRight: {
         type: String,
         default: ""
      },      
      btnRole: {
         type: String,
         required: true
         // validation: save,saveas,delete
      },
      btnTitle: {
         type: String,
         default: "Save"
      },
      debug: {
         type: Boolean,
         default: false
      },      
      dialogTitle: {
         type: String,
         default: ""
      },
      submitFunc: {
         type: Function,
         required: true
      },
      // V251027
      confirmMessage: {
         type: String,
         default: ""
      },
   },

   data() {
      return {
         panels: [0],
         selectedItem: {},
         dialogSave: false,
         mySharedWith: new SharedWith({}),
         formValues: JSON.parse(JSON.stringify(FORM_VALUES)),
         rules: {
            required: value => !!value || "Value is required!",
            arrayRequired: value => value.length > 0 || "Value is required!",
            numbers: value => {
               const result = [];
               const ids = _stringToArray(value);
               ids.forEach(id => { 
                  if (!NUMBER_PATTERN.test(id) && result.findIndex(r => r === `'${id}' is invalid!`) === -1) 
                     result.push(`'${id}' is invalid!`);
               });
               return result.length === 0 || result.join(', ');
            },
            emails: value => {
               const result = [];
               const emails = _stringToArray(value);
               emails.forEach(e => {
                  if (!EMAIL_PATTERN.test(e) && result.findIndex(r => r === `'${e}' is invalid!`) === -1)
                     result.push(`'${e}' is invalid!`); });
               return result.length === 0 || result.join(', ');
            },
            duplicate: value => {
               const result = [];
               const vals = _stringToArray(value);
               const uniqueVals = new Set(vals);
               uniqueVals.forEach(val => { 
                  if (vals.filter(v => v === val).length > 1) result.push(`'${val}' is duplicate!`);
               });
               return result.length === 0 || result.join(', ');
            },
            differentName: value => (!!value && value.toLowerCase() != this.value.name.toLowerCase()) || 'The new name cannot be the same as the current name!'
         },
         paidItems: [],
         saidItems: [],
         aidItems: [],
         userItems: [
            { text: 'All', value: 'all', disabled: false },
            { text: 'Current User', value: 'current'},
            { text: 'Custom...', value: 'custom'}
         ],
         errMsg: '',
         overlay: false,

         newDashName: '',
         newDashNameRules: [],
         isNewDashNameDisabled: false,
         newTags: [],
         tagItems: [
            { text: "Channel", value: "channel" },
            { text: "Event", value: "event" },
            { text: "Form", value: "form" },
            { text: "Inbound", value: "inbound" },
            { text: "Goal", value: "goal" },
            { text: "Page", value: "page" },
            { text: "Program", value: "program" },
            { text: "Outbound", value: "outbound" },
            { text: "Schedule", value: "schedule" },
            { text: "Score", value: "score" }
         ],

         dialogDelete: false,
         currDashName: '',
         isFormSaveValid: false
      }
   },

   computed: {
      token() {
         return this.$store.getters.token;
      },

      myPaid() {
         if (this.jwt.paid) return this.jwt.paid;
         else return this.jwt.aid;
      },

      myDialogTitle() {
         if (this.dialogTitle)
            return this.dialogTitle;
         else if (this.value.name)
            return this.btnRole === 'save' ? `Save '${this.value.name}' Dashboard` : `Save '${this.value.name}' as a New Dashboard:`;
         else
            return 'Save Your New Dashboard:';
      },

      isDeveloper() {
         return developersAccess().includes(this.jwt.email);
      }
   },

   watch: {
      token: {
         immediate: true,
         handler(val) {
            try {
               this.jwt = {};
               if (val) {
                  this.jwt = JSON.parse(Buffer.from(this.token.split('.')[1], 'base64'));
                  _log(`jwt=${JSON.stringify(this.jwt)}`);
               }
            } catch (error) {
               alert(`Exception while parsing token: ${JSON.stringify(error)}`);
            }
         }
      },

      dialogSave (val) {
         if (val) {
            setTimeout(() => {
               // if (!this.confirmMessage || confirm(this.confirmMessage))
               this.initDialogSave();
            }, 10);
         } 
      },

      dialogDelete (val) {
         if (val) {
            setTimeout(() => {
               this.initDialogDelete();
            }, 10);
         } 
      }
   },

   methods: {
      btnClicked() {
         // V251027
         if (this.confirmMessage && !confirm(this.confirmMessage)) return;

         if (this.value.name) {
            switch (this.btnRole) {
               case 'save':
                  this.newDashName = this.value.name;
                  this.newDashNameRules = [];
                  this.isNewDashNameDisabled = true;
                  this.dialogSave = true;
                  break;
               case 'saveas':
                  this.newDashName = 'Copy of ' + this.value.name;
                  this.newDashNameRules = [ this.rules.required, this.rules.differentName ];
                  this.isNewDashNameDisabled = false;
                  this.dialogSave = true;
                  break;
               case 'delete':
                  this.dialogDelete = true;
                  break;
               default:
                  break;
            }
         } else {
            switch (this.btnRole) {
               case 'save':
               case 'saveas':
                  this.newDashNameRules = [ this.rules.required ];
                  this.dialogSave = true;
                  break;
               default:
                  break;
            }
         }
      },

      initDialogSave() {
         this.newTags = this.value.tags ? [...this.value.tags] : [];
         if (!this.isNewDashNameDisabled && this.$refs.newDashName)
            this.$refs.newDashName.focus();

         // this.value.sharedWith = {
         //    paids: ['17721'],
         //    aids: ['17721','1222'],
         //    users: ['btalebpour@mindfireinc.com', 'btalebpour@mindfiremail.info']
         // };

         this.mySharedWith = new SharedWith(this.value.sharedWith);

         this.paidItems = [
            { text: 'None', value: 'all'},
            { text: 'Current Parent', value: 'current'}
         ];

         this.saidItems = [
            { text: 'None', value: 'all'},
            { text: 'Current Account', value: 'current'},
            { text: 'Current Parent', value: 'currentP'}
         ];

         if (this.isDeveloper) {
            this.paidItems.push({ text: 'Custom...', value: 'custom'});
            this.saidItems.push({ text: 'Custom...', value: 'custom'});
         } 
         
         if (this.value.sharedWith) {
            this.formValues.paid = this.getSelectedValue(this.value.sharedWith?.paids, this.myPaid);
            this.formValues.said = this.getSelectedValue(this.value.sharedWith?.aids, this.jwt.aid, this.myPaid);
            this.formValues.user = this.getSelectedValue(this.value.sharedWith?.users, this.jwt.email);  //V260105
         }
      },

      getSelectedValue(arrVal, val, pVal) {
         // alert(`in getSelectedValue(): arrVal=${JSON.stringify(arrVal)}, val=${val}, pVal=${pVal}`);
         if (!arrVal || !arrVal.length) return 'all';

         if (arrVal.length === 1 && arrVal[0] == val) return 'current';

         if (arrVal.length === 1 && arrVal[0] == pVal) return 'currentP';

         return 'custom';
      },

      getHint(value, name) {
         if (this.mySharedWith) {
            if (value) {
               const len = _stringToArray(value).length;
               return `${len} ${name}${len > 1 ? 's have' : ' has'} been specified.`;
            } else
               return 'separate different ' + name + 's with comma.';
         } else return '';
      },

      tagsChanged() {
         this.$refs.formSave.validate();
      },

      paidChanged(value) {
         this.saidItems[0].disabled = Boolean(value === 'all');

         if (value === 'current')
            this.mySharedWith.paids = this.myPaid.toString();
         else if (value === 'all')
            this.mySharedWith.paids = '';
         else
            this.$nextTick(() =>
               this.$refs.paidTextarea.focus()
            );

         this.$refs.formSave.validate();
      },

      saidChanged(value) {
         this.paidItems[0].disabled = Boolean(value === 'all');

         if (value === 'current')
            this.mySharedWith.saids = this.jwt.aid.toString();
         if (value === 'currentP')
            this.mySharedWith.saids = this.myPaid.toString();
         else if (value === 'all')
            this.mySharedWith.saids = '';
         else
            this.$nextTick(() =>
               this.$refs.saidTextarea.focus()
            );

         this.$refs.formSave.validate();
      },

      userChanged(value) {
         if (value === 'current')
            this.mySharedWith.users = this.jwt.email;
         else if (value === 'all')
            this.mySharedWith.users = '';
         else
            this.$nextTick(() =>
               this.$refs.userTextarea.focus()
            );

         this.$refs.formSave.validate();
      },

      async saveClicked() {
         if (!this.$refs.formSave.validate()) return;

         _log('in saveClicked(): this.mySharedWith=' + JSON.stringify(this.mySharedWith));
         this.overlay = true;

         const sharedWith = {
            paids: _stringToArray(this.mySharedWith.paids),
            aids: _stringToArray(this.mySharedWith.saids),
            users: _stringToArray(this.mySharedWith.users)
         }

         _log('in saveClicked(): sharedWith=' + JSON.stringify(sharedWith));
         const dashboard = JSON.parse(JSON.stringify(this.value));
         dashboard.name = this.newDashName;
         dashboard.tags = this.newTags;
         dashboard.sharedWith = sharedWith;
         // alert('sharedWith=' + JSON.stringify(sharedWith));
         this.errMsg = await this.submitFunc(dashboard);
         if (!this.errMsg) {
            this.$emit('input', dashboard);
            this.$emit('submit', dashboard);
            this.closeDialogSave();
         }
         this.overlay = false;
      },

      closeDialogSave () {
         this.$refs.formSave?.resetValidation();
         this.dialogSave = false;

         setTimeout(() => {
            this.newDashName = '';
            this.isNewDashNameDisabled = false;
            this.newTags = [];
            this.mySharedWith = new SharedWith({});
            this.formValues = JSON.parse(JSON.stringify(FORM_VALUES));
            this.errMsg = '';
         }, 10);
      },

      initDialogDelete () {
         if (this.$refs.currDashName)
            this.$refs.currDashName.focus();
      },

      async deleteClicked() {
         if (!this.$refs.formDelete.validate()) return;

         this.overlay = true;
         _log('in deleteClicked(): currDashName=' + this.currDashName);
         this.errMsg = await this.submitFunc(this.value);
         if (!this.errMsg) {
            this.$emit('submit', this.value);
            this.closeDialogDelete();
         }
         this.overlay = false;
      },

      closeDialogDelete () {
         this.$refs.formDelete?.resetValidation();

         this.dialogDelete = false;
         setTimeout(() => {
            this.currDashName = '';
            this.errMsg = '';
         }, 10);
      }
   },

   mounted() {
      DEBUG = this.debug;
   }
}
</script>

<style scoped>
   .v-textarea textarea {
      line-height: 1rem;
      padding: 8px 8px 8px 0;
   }
   .v-expansion-panel-header, .v-expansion-panel-header--active {
      padding: 0 16px;
      min-height: 40px !important;
   }
   .v-expansion-panel-content__wrap {
      padding-top: 20px;
      padding-bottom: 20px;
   }
</style>